import { ICartItem } from '@/api/types/cart';
import ProductImageWithLenses from '@/components/Product/ProductImageWithLenses';
import ZeeloolCheckBox from '@/components/ZeeloolCheckBox';
import { EPrescriptionType } from '@/utils/enums/goods';
import { setSession } from '@/utils/tools';
import { useTranslations } from '@/i18n/use-translations';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import React from 'react';

interface InvalidCartItemPCProps {
  cartItemData: ICartItem;
  batchFlag: boolean;

  // 从useCartItem hooks传来的状态和方法
  isGlassesGrayImage: boolean;
  selected?: boolean;

  // 方法
  formatPrice: (price: number | string) => string;
  getProductImage: () => string;
  getProductName: () => string;
  getAllPrices: () => any;
  getPrescriptionPrice: () => { price: number; sell_price: number };
  handleDelete: () => void;
  handleMoveToWishlist: () => void;
  // 不可结算商品选中回调
  handleInvalidSelectChange: (checked: boolean) => void;
}

export const InvalidCartItemPC: React.FC<InvalidCartItemPCProps> = ({
  cartItemData,
  batchFlag,

  // hooks状态和方法
  isGlassesGrayImage,
  selected = false,

  // hooks方法
  formatPrice,
  getProductImage,
  getProductName,
  getAllPrices,
  getPrescriptionPrice,
  handleDelete,
  handleMoveToWishlist,
  handleInvalidSelectChange,
}) => {
  const globalT = useTranslations('cart');
  const t = useTranslations('cart.invalidCartItem');
  const tPriceA11y = useTranslations('goodsDetail.clientPrice');
  const router = useRouter();

  // 从cartItemData中获取基础信息
  const productName = getProductName();
  const productColor = cartItemData.product?.color || '';
  const isGiftItem = cartItemData.type === 4;
  const isGlassProduct = cartItemData.product?.is_glass === 1;

  // 判断商品状态
  const getItemStatus = () => {
    const { product, stock, type } = cartItemData;
    if (type === 4) return 'ended';
    if (product?.status === 2) return 'no-restock';
    if (stock < 1) return 'sold-out';
    return null;
  };

  const itemStatus = getItemStatus();

  // 处理商品图片点击
  const handleProductImageClick = (event: React.MouseEvent<HTMLAnchorElement>) => {
    event.preventDefault(); // 阻止a标签默认跳转

    // 在批量模式下阻止跳转
    if (batchFlag) {
      return;
    }

    if (cartItemData.product?.shop_sku) {
      setSession('zl_goodsDetailEnterPageScene', '购物车页商品条目');

      router.push(`/goods-detail/${cartItemData.product.shop_sku}`);
    }
  };

  // 获取产品图片的className
  const getProductImageClassName = () => {
    let widthClass = '!w-full';
    if (cartItemData.product?.is_glass === 1) {
      widthClass = '!w-[80%]'; // PC端80%
    }
    const blendClass = isGlassesGrayImage ? 'mix-blend-normal' : 'mix-blend-multiply';
    return `${widthClass} object-cover ${blendClass}`;
  };

  // 渲染状态标签覆盖层
  const renderStatusOverlay = () => {
    if (!itemStatus) return null;

    let statusText = '';
    switch (itemStatus) {
      case 'ended':
        statusText = t('ended');
        break;
      case 'no-restock':
        statusText = t('noRestock');
        break;
      case 'sold-out':
        statusText = t('soldOut');
        break;
      default:
        break;
    }

    return (
      <div className="absolute inset-0 bg-white/50 flex items-center justify-center rounded-[6px]">
        <div className="flex items-center justify-center w-[80px] h-[80px] rounded-full text-center bg-black/70 text-white text-[12px] whitespace-pre-line font-medium">
          {statusText}
        </div>
      </div>
    );
  };

  // 从价格计算hook获取所有价格信息（参考CartDetailPopup）
  const allPrices = getAllPrices();
  const { product: productPrice } = allPrices;
  const hasDiscount = productPrice.price !== productPrice.sell_price && productPrice.price > 0;

  // 是否有处方类型
  const hasPresType =
    isGlassProduct &&
    cartItemData.option?.prescription_type &&
    cartItemData.option.prescription_type !== EPrescriptionType.FRAME_ONLY;

  return (
    <div className="pt-[16px] pr-[40px] pl-[16px] pb-[24px] bg-white">
      {/* 顶部区域：复选框、Gift标签、操作按钮 */}
      <div className="relative flex justify-between items-center pb-[8px]">
        <div className="flex">
          <div className="w-[20px]">
            {/* 批量模式下显示复选框 */}
            {batchFlag && (
              <div className="relative bottom-[2px]">
                <ZeeloolCheckBox
                  checked={selected}
                  onChange={(e) => handleInvalidSelectChange(e.target.checked)}
                  fontSize={20}
                />
              </div>
            )}
          </div>
          <div className="pl-[20px]">
            {/* Gift 标签 */}
            {isGiftItem && (
              <span className="bg-[#baacff] block px-[9px] h-[20px] leading-[20px] rounded-[4px] text-[12px] text-primary-black mr-[8px] opacity-50">
                {t('gift')}
              </span>
            )}
          </div>
        </div>

        {/* 操作按钮  */}
        <div className="flex justify-end items-center space-x-4">
          {[1, 4].includes(cartItemData.type || 1) && (
            <>
              <button
                className="text-[14px] text-[#808080] underline mr-[16px] cursor-pointer hover:text-[#666666]"
                onClick={handleMoveToWishlist}
              >
                {cartItemData.product?.is_wish === 1
                  ? t('removeFromWishlist')
                  : t('moveToWishlist')}
              </button>
              <button
                className="text-[14px] text-[#808080] underline cursor-pointer hover:text-[#666666]"
                onClick={handleDelete}
              >
                {t('delete')}
              </button>
            </>
          )}
        </div>
      </div>

      {/* 主体区域：图片 + 信息 */}
      <div className="flex items-start space-x-4 pl-[40px]">
        {/* 商品图片 */}
        <Link
          href={`/goods-detail/${cartItemData.product?.shop_sku}`}
          className={`relative w-[180px] h-[180px] 2xl:w-[220px] 2xl:h-[220px] bg-[#eeeeee] flex items-center justify-center overflow-hidden rounded-[6px] ${batchFlag ? 'cursor-not-allowed pointer-events-none' : 'cursor-pointer'}`}
          onClick={handleProductImageClick}
        >
          <ProductImageWithLenses
            imgUrl={getProductImage()}
            product={cartItemData.product || {}}
            lens={cartItemData.lens || {}}
            className={getProductImageClassName()}
          />
          {/* 状态覆盖层 */}
          {renderStatusOverlay()}
        </Link>

        {/* 商品信息区域 */}
        <div className="flex-1 min-w-0 flex flex-col ml-[40px] 2xl:ml-[70px]">
          <div className="flex-1 space-y-5 pt-[20px] flex flex-col">
            {/* 商品名称 */}
            <div className="mb-[7px] 2xl:mb-[12px] flex items-center opacity-50">
              <span className="font-bb text-[14px] md:text-[16px]">
                {isGlassProduct ? globalT('cartItem.frame2') : globalT('cartItem.product')}
              </span>
              <span className="ml-[12px] text-[14px] md:text-[16px]">{productName}</span>
            </div>

            {/* 颜色和价格 */}
            <div className="flex items-center justify-between mb-[7px] 2xl:mb-[12px] opacity-50">
              <div className="flex items-center text-primary-black h-[28px] leading-[28px]">
                <span className="font-bb text-[14px] md:text-[16px]">
                  {globalT('GiftsWithCartItem.color')}:
                </span>
                <span className="ml-[12px] text-[14px] md:text-[16px]">{productColor}</span>
              </div>
              <div className="flex items-end text-[14px] md:text-[16px]">
                {/* 如果是赠品（type=4），显示0.00 */}
                {cartItemData.type === 4 ? (
                  <span className="font-bb">{formatPrice('0.00')}</span>
                ) : (
                  <>
                    {hasDiscount && (
                      <span className="text-[#a9a9a9] line-through">
                        <del
                          aria-label={tPriceA11y('srOriginal') + formatPrice(productPrice.price)}
                        >
                          {formatPrice(productPrice.price)}
                        </del>
                      </span>
                    )}
                    <span
                      className="font-bb ml-[5px]"
                      aria-label={tPriceA11y('srCurrent') + formatPrice(productPrice.sell_price)}
                    >
                      {formatPrice(productPrice.sell_price)}
                    </span>
                  </>
                )}
              </div>
            </div>

            {/* 处方类型和镜片价格 */}
            {hasPresType && (
              <div className="flex items-center justify-between mb-[7px] 2xl:mb-[12px] opacity-50">
                <div className="flex items-center">
                  <span className="font-bb text-[14px] md:text-[16px]">{t('prescription')}:</span>
                  <span className="ml-[12px] text-[14px] md:text-[16px]">
                    {cartItemData?.option?.prescription_type_name ||
                      cartItemData.option?.prescription_type}
                  </span>
                </div>
                <div className="text-right">
                  <span className="text-[14px] md:text-[16px] font-bb">
                    {formatPrice(getPrescriptionPrice().sell_price)}
                  </span>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
